import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiPlay, FiRefreshCw } from 'react-icons/fi';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { API_ENDPOINTS, getToken } from '../config/api';
import { MOCK_GRADING_CRITERIA } from '../constants/mockCriteria';
import { mockGradingAPI, MockSubmissionSummary, MockSubmissionDetail, recordingsAPI } from '../services/api';
import '../components/Dashboard.css';
import './MockGradingPage.css';

/** Set true to log raw graded-list API response in the UI (temporary debugging). */
const MOCK_GRADED_TAB_DEBUG = false;

interface MockGradingPageProps {
  onLogout?: () => void;
  userEmail?: string;
}

const MockGradingPage: React.FC<MockGradingPageProps> = ({ onLogout, userEmail }) => {
  const { submissionId } = useParams<{ submissionId?: string }>();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [submissions, setSubmissions] = useState<MockSubmissionSummary[]>([]);
  const [detail, setDetail] = useState<MockSubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grades, setGrades] = useState<Record<number, Record<string, number>>>({});
  const [adminOverallFeedback, setAdminOverallFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [listTab, setListTab] = useState<'pending' | 'graded'>('pending');
  const [gradedListDebug, setGradedListDebug] = useState<string | null>(null);

  useEffect(() => {
    if (submissionId) {
      fetchDetail();
    } else {
      fetchList();
    }
  }, [submissionId, listTab]);

  const fetchList = async () => {
    try {
      setLoading(true);
      setError(null);
      setGradedListDebug(null);

      if (MOCK_GRADED_TAB_DEBUG && listTab === 'graded') {
        const url = `${API_ENDPOINTS.ADMIN_MOCK_SUBMISSIONS}?status=graded`;
        const token = getToken();
        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Token ${token}` } : {}),
          },
        });
        const text = await res.text();
        let body: unknown;
        try {
          body = JSON.parse(text);
        } catch {
          body = { _parseError: 'response was not JSON', snippet: text.slice(0, 800) };
        }
        const extracted =
          Array.isArray(body)
            ? (body as MockSubmissionSummary[])
            : body && typeof body === 'object' && Array.isArray((body as { results?: unknown }).results)
              ? (body as { results: MockSubmissionSummary[] }).results
              : null;
        setGradedListDebug(
          JSON.stringify(
            {
              requestUrl: url,
              httpStatus: res.status,
              httpOk: res.ok,
              responseBody: body,
              extractedCount: extracted?.length ?? null,
              extractedIsArray: Array.isArray(extracted),
            },
            null,
            2
          )
        );
        if (!res.ok) {
          const errObj = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
          const msg =
            (typeof errObj.error === 'string' && errObj.error) ||
            (typeof errObj.detail === 'string' && errObj.detail) ||
            `HTTP ${res.status}`;
          throw new Error(msg);
        }
        setSubmissions(extracted ?? []);
      } else {
        const data = await mockGradingAPI.listSubmissions(listTab);
        setSubmissions(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
      setSubmissions([]);
      if (MOCK_GRADED_TAB_DEBUG && listTab === 'graded') {
        setGradedListDebug((prev) =>
          prev
            ? `${prev}\n\n--- fetch catch ---\n${JSON.stringify({ message: err?.message || String(err) }, null, 2)}`
            : JSON.stringify({ fetchError: err?.message || String(err) }, null, 2)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async () => {
    if (!submissionId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await mockGradingAPI.getDetail(Number(submissionId));
      setDetail(data);
      const initial: Record<number, Record<string, number>> = {};
      data.audios.forEach((a) => {
        initial[a.submission_audio_id] = {};
        MOCK_GRADING_CRITERIA.forEach((c) => {
          const fromApi =
            data.readonly && a.criterion_scores && typeof a.criterion_scores[c.key] === 'number'
              ? a.criterion_scores[c.key]
              : undefined;
          const v = fromApi != null && fromApi >= 1 && fromApi <= 5 ? fromApi : 3;
          initial[a.submission_audio_id][c.key] = v;
        });
      });
      setGrades(initial);
      setAdminOverallFeedback(data.readonly ? (data.admin_overall_feedback ?? '') : '');
    } catch (err: any) {
      setError(err.message || 'Failed to load submission');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const updateGrade = (submissionAudioId: number, criterion: string, value: number) => {
    setGrades((prev) => ({
      ...prev,
      [submissionAudioId]: {
        ...(prev[submissionAudioId] || {}),
        [criterion]: value,
      },
    }));
  };

  const getOverallScore = (): number => {
    if (!detail) return 0;
    let total = 0;
    let count = 0;
    detail.audios.forEach((a) => {
      const g = grades[a.submission_audio_id];
      if (g) {
        const vals = Object.values(g).filter((v) => v >= 1 && v <= 5);
        total += vals.reduce((s, v) => s + v, 0);
        count += vals.length;
      }
    });
    return count > 0 ? Math.round((total / count) * 100) / 100 : 0;
  };

  const handleSubmitResults = async () => {
    if (!detail || submitting) return;
    const gradesArray = detail.audios.map((a) => {
      const g = grades[a.submission_audio_id] || {};
      return {
        submission_audio_id: a.submission_audio_id,
        distortion: g.distortion ?? 3,
        unjustified_omission: g.unjustified_omission ?? 3,
        unjustified_insertion: g.unjustified_insertion ?? 3,
        inappropriate_register: g.inappropriate_register ?? 3,
        unidiomatic_expression: g.unidiomatic_expression ?? 3,
        grammar_syntax_error: g.grammar_syntax_error ?? 3,
        spelling_error: g.spelling_error ?? 3,
        punctuation_error: g.punctuation_error ?? 3,
      };
    });
    try {
      setSubmitting(true);
      await mockGradingAPI.submitResults(detail.id, gradesArray, adminOverallFeedback.trim());
      navigate('/admin/mock-grading');
    } catch (err: any) {
      setError(err.message || 'Failed to submit results');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePlaySystemAudio = async (audioId: number) => {
    try {
      const url = await recordingsAPI.fetchAudioBlob(audioId);
      const a = new Audio(url);
      a.play();
      a.onended = () => URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to play audio', e);
    }
  };

  const handlePlayUserAudio = async (audioId: number) => {
    try {
      const url = await recordingsAPI.fetchAudioBlob(audioId);
      const a = new Audio(url);
      a.play();
      a.onended = () => URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to play audio', e);
    }
  };

  const layoutShell = (children: React.ReactNode) => (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userEmail || ''}
          organization="NAATI"
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">{children}</div>
        </div>
      </div>
    </div>
  );

  // List view
  if (!submissionId) {
    return layoutShell(
      <>
        <motion.div
          className="dashboard-welcome"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div>
            <h1 className="dashboard-title">Mock Grading</h1>
            <p className="dashboard-subtitle">Review pending tests or open graded submissions (read-only)</p>
          </div>
        </motion.div>

        <div className="mock-grading-tabs" role="tablist" aria-label="Submission filter">
          <button
            type="button"
            role="tab"
            aria-selected={listTab === 'pending'}
            className={`mock-grading-tab${listTab === 'pending' ? ' mock-grading-tab-active' : ''}`}
            onClick={() => setListTab('pending')}
          >
            Pending
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={listTab === 'graded'}
            className={`mock-grading-tab${listTab === 'graded' ? ' mock-grading-tab-active' : ''}`}
            onClick={() => setListTab('graded')}
          >
            Graded
          </button>
        </div>

        {MOCK_GRADED_TAB_DEBUG && listTab === 'graded' && gradedListDebug !== null && !loading && (
          <details className="mock-graded-debug-wrap" style={{ marginBottom: '1rem' }}>
            <summary style={{ cursor: 'pointer', color: '#aaa', fontSize: '0.85rem' }}>
              Graded list — API debug (temporary)
            </summary>
            <pre className="mock-graded-debug">{gradedListDebug}</pre>
          </details>
        )}

        {loading ? (
          <div className="mock-grading-loading">
            {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string }>, { className: 'spinner' })}
            <p>Loading submissions...</p>
          </div>
        ) : error ? (
          <div className="mock-grading-error">
            <p>{error}</p>
            <button type="button" onClick={fetchList}>
              Retry
            </button>
          </div>
        ) : submissions.length === 0 ? (
          <div className="mock-grading-empty">
            <p>{listTab === 'pending' ? 'No pending mock submissions.' : 'No graded mock submissions yet.'}</p>
          </div>
        ) : (
          <div className="mock-submissions-list">
            {submissions.map((s) => (
              <motion.div
                key={s.id}
                className="mock-submission-card"
                whileHover={{ y: -2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                onClick={() => navigate(`/admin/mock-grading/${s.id}`)}
              >
                <div className="mock-submission-user">{s.user_email}</div>
                <div className="mock-submission-topic">{s.dialogue_name}</div>
                <div className="mock-submission-meta">
                  {s.category_name} • {s.audios_count} audios • {new Date(s.created_at).toLocaleDateString()}
                  {listTab === 'graded' && typeof s.overall_score === 'number' ? (
                    <span className="mock-submission-overall"> • Overall {s.overall_score.toFixed(2)} / 5</span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="mock-submission-grade-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/mock-grading/${s.id}`);
                  }}
                >
                  {listTab === 'graded' ? 'View' : 'Grade'}
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </>
    );
  }

  // Detail / Grading view
  if (loading || !detail) {
    return layoutShell(
      <>
        {loading ? (
          <div className="mock-grading-loading">
            {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string }>, { className: 'spinner' })}
            <p>Loading...</p>
          </div>
        ) : (
          <div className="mock-grading-error">
            <p>{error || 'Submission not found'}</p>
            <button type="button" onClick={() => navigate('/admin/mock-grading')}>
              Back to list
            </button>
          </div>
        )}
      </>
    );
  }

  return layoutShell(
    <div className="mock-grading-detail">
          <div className="mock-grading-nav">
            <button type="button" onClick={() => navigate('/admin/mock-grading')}>
              {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 18 })}
              Back to list
            </button>
          </div>
          <h1 className="dashboard-title">
            {detail.readonly ? 'Review: ' : 'Grade: '}
            {detail.dialogue_name}
          </h1>
          <p className="dashboard-subtitle mock-grading-user-line">User: {detail.user_email}</p>

          {detail.readonly && detail.overall_score != null ? (
            <div className="mock-overall-preview mock-overall-preview-final">
              <strong>Final overall score:</strong> {detail.overall_score.toFixed(2)} / 5.00
            </div>
          ) : !detail.readonly ? (
            <div className="mock-overall-preview">
              <strong>Preview overall score:</strong> {getOverallScore().toFixed(2)} / 5.00
            </div>
          ) : null}

          {detail.audios.map((audio, idx) => (
            <div key={audio.submission_audio_id} className="mock-audio-grading-card">
              <h3>Audio {idx + 1}</h3>
              <div className="mock-audio-row">
                <div className="mock-audio-col">
                  <h4>System voice</h4>
                  <p className="mock-source-text">{audio.system_audio.source_text || '(no source text)'}</p>
                  <button
                    type="button"
                    className="mock-play-btn"
                    onClick={() => handlePlaySystemAudio(audio.system_audio.id)}
                  >
                    {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 14 })}
                    Play
                  </button>
                </div>
                <div className="mock-audio-col">
                  <h4>User recording</h4>
                  <p className="mock-transcript">{audio.user_recording.transcript || '(no transcript)'}</p>
                  <button
                    type="button"
                    className="mock-play-btn"
                    onClick={() => handlePlayUserAudio(audio.user_recording.id)}
                  >
                    {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 14 })}
                    Play
                  </button>
                </div>
              </div>
              {(audio.system_audio.translations || []).length > 0 && (
                <div className="mock-expected-translations">
                  <h4>Expected translations</h4>
                  <ul>
                    {(audio.system_audio.translations || []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className={`mock-sliders${detail.readonly ? ' mock-sliders-readonly' : ''}`}>
                {MOCK_GRADING_CRITERIA.map((c) => (
                  <div key={c.key} className="mock-slider-row">
                    <label>{c.label}</label>
                    {detail.readonly ? (
                      <span className="mock-grade-readonly-val">
                        {grades[audio.submission_audio_id]?.[c.key] ?? '—'}
                      </span>
                    ) : (
                      <>
                        <input
                          type="range"
                          min={1}
                          max={5}
                          step={1}
                          value={grades[audio.submission_audio_id]?.[c.key] ?? 3}
                          onChange={(e) => updateGrade(audio.submission_audio_id, c.key, Number(e.target.value))}
                        />
                        <span>{grades[audio.submission_audio_id]?.[c.key] ?? 3}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {detail.readonly && adminOverallFeedback.trim() ? (
            <div className="mock-readonly-feedback-block">
              <h4 className="mock-readonly-feedback-title">Feedback sent to candidate</h4>
              <p className="mock-readonly-feedback-body">{adminOverallFeedback.trim()}</p>
            </div>
          ) : null}

          {!detail.readonly ? (
            <div className="mock-admin-feedback-field">
              <label htmlFor="mock-admin-overall-feedback">Overall feedback for the candidate (optional)</label>
              <textarea
                id="mock-admin-overall-feedback"
                className="mock-admin-feedback-textarea"
                rows={5}
                maxLength={8000}
                placeholder="Optional message about this mock test (shown on the candidate’s result page)."
                value={adminOverallFeedback}
                onChange={(e) => setAdminOverallFeedback(e.target.value)}
              />
              <span className="mock-admin-feedback-hint">{adminOverallFeedback.length} / 8000</span>
            </div>
          ) : null}

          {error && <p className="mock-grading-err-msg">{error}</p>}
          {!detail.readonly ? (
            <button
              type="button"
              className="mock-submit-results-btn"
              onClick={handleSubmitResults}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Results'}
            </button>
          ) : null}
    </div>
  );
};

export default MockGradingPage;
